// ─── Onboarding Dashboard (Spec C §8) ───────────────────────────────────────
//
// Rijkere KPI-DTO voor de aparte `/onboarding/dashboard`-pagina in de
// backoffice. Bestaande `OnboardingStatsDTO` + `OnboardingDashboardStatsDTO`
// (uit `onboarding.ts`) blijven ongewijzigd — die voeden de KPI-rij op de
// lijst-pagina.
//
// Deze DTO bundelt 4 KPI's + per-status-tellingen + 4 signalen-lijsten + de
// 10 meest recent gestarte onboardings. Endpoint: GET /onboarding/dashboard.

// batch-42 onboarding (FO-369) — telling per blokkade-reden op het dashboard.
import type { OnboardingBlokkadeReden } from './onboarding-blokkade'

// ─── Status-union (Spec C §3.1) ──────────────────────────────────────────────
//
// Geconsolideerde widened union — vervangt op termijn de v1-narrow
// `OnboardingStatus` (4 waarden) in `./onboarding.ts`. Spiegelt 1:1 de Prisma-
// enum `OnboardingStatus` na de spec-C-uitbreiding (PR C0).
//
// Refereert aan de TODO in `goedkeuringen-inbox.ts` (PR C9 — "consolideer naar
// één bron"). Vanaf hier is dit de SSoT voor alle UI- en API-DTOs die de
// extended status-set tonen.

export const ONBOARDING_STATUS_VALUES = [
  'CONCEPT',
  'WACHT_OP_MEDEWERKER',
  'IN_BEHANDELING',
  'IN_CONTROLE',
  'WACHT_OP_HANDTEKENING',
  'ASSETS_TOEWIJZEN',
  'GEREED_VOOR_START',
  'ACTIEF',
  'AFGEROND',
  'GEANNULEERD',
] as const

export type OnboardingStatusValue = (typeof ONBOARDING_STATUS_VALUES)[number]

/**
 * FO-414 — de drie eindstatussen: die houden het her-onboard-slot NIET bezet
 * (conflict-check in POST /onboarding, partiële unique
 * `onboardings_employeeId_active_key`, blokkade-resolver, medewerkerpagina).
 * Eén bron; de migratie-SQL herhaalt dezelfde drie waarden letterlijk.
 */
export const ONBOARDING_EIND_STATUSSEN = [
  'GEANNULEERD',
  'AFGEROND',
  'ACTIEF',
] as const satisfies readonly OnboardingStatusValue[]

export const ONBOARDING_STATUS_VALUE_LABELS: Record<OnboardingStatusValue, string> = {
  CONCEPT:               'Concept',
  WACHT_OP_MEDEWERKER:   'Wacht op medewerker',
  IN_BEHANDELING:        'In behandeling',
  IN_CONTROLE:           'Wacht op HR-controle',
  WACHT_OP_HANDTEKENING: 'Wacht op handtekening',
  ASSETS_TOEWIJZEN:      'Assets toewijzen',
  GEREED_VOOR_START:     'Gereed voor start',
  ACTIEF:                'Actief',
  AFGEROND:              'Afgerond',
  GEANNULEERD:           'Geannuleerd',
}

/**
 * Badge-variant per status voor de StatusBadge-component. SSoT voor de volle
 * 10-waarde C-flow — vervangt de losse 4-waarde `ONBOARDING_STATUS_BADGE_VARIANT`
 * (in `./onboarding.ts`) op alle plekken die de extended status-set tonen
 * (detail-, list- en dashboard-pagina).
 */
export const ONBOARDING_STATUS_VALUE_BADGE_VARIANT: Record<
  OnboardingStatusValue,
  'info' | 'warning' | 'success' | 'danger' | 'secondary'
> = {
  CONCEPT:               'secondary',
  WACHT_OP_MEDEWERKER:   'warning',
  IN_BEHANDELING:        'warning',
  IN_CONTROLE:           'info',
  WACHT_OP_HANDTEKENING: 'info',
  ASSETS_TOEWIJZEN:      'info',
  GEREED_VOOR_START:     'info',
  ACTIEF:                'success',
  AFGEROND:              'success',
  GEANNULEERD:           'danger',
}

// ─── Signalen — per-item shapes ──────────────────────────────────────────────

/**
 * Onboarding waar HR meer dan 3 dagen wacht op input van de medewerker.
 * Triggered door status `WACHT_OP_MEDEWERKER` (of CONCEPT zonder activiteit)
 * waarbij `ingediendOp` > 3 dagen geleden.
 */
export interface SignaalWachtOpMedewerker {
  onboardingId:  string
  voornaam:      string
  dagenWachtend: number
}

/**
 * Onboarding met een deadline binnen 7 dagen die nog niet voltooid is.
 * `dagenTot` is negatief wanneer de deadline al voorbij is.
 */
export interface SignaalDeadlineNabij {
  onboardingId: string
  voornaam:     string
  deadline:     string // YYYY-MM-DD
  dagenTot:     number
}

/**
 * Openstaande 4-ogen-review (status IN_AFWACHTING) — de reviewer moet nog
 * goed/afkeuren. `rol` toont welke rol (recruiter/teamleider vs HR/Kasper).
 */
export interface SignaalReview4OgenOpen {
  onboardingId:  string
  voornaam:      string
  rol:           string
  dagenWachtend: number
}

/**
 * Onboarding waar geen assets zijn gereserveerd ondanks dat de status
 * `ASSETS_TOEWIJZEN` of verder is. Signaleert dat HR voorraad moet checken.
 */
export interface SignaalAssetsOntbreken {
  onboardingId: string
  voornaam:     string
}

// ─── Recent-rij ──────────────────────────────────────────────────────────────

/**
 * 10 meest recent gestarte onboardings. Compact rij voor de "recent"-tabel
 * onderaan het dashboard.
 */
export interface OnboardingDashboardRecentRow {
  id:          string
  voornaam:    string
  achternaam:  string
  status:      OnboardingStatusValue
  startDatum:  string // YYYY-MM-DD — `ingediendOp` (datum van aanmaak) fallback
  dagenLopend: number
}

// ─── Dashboard-DTO ───────────────────────────────────────────────────────────

/**
 * Spec C §8.2 — Dashboard-stats voor `/onboarding/dashboard`-pagina.
 *
 * `totaalActief` = aantal onboardings met status NIET in
 * [AFGEROND, GEANNULEERD, ACTIEF].
 *
 * `perStatus` = tellingen per `OnboardingStatusValue`; bevat alle statuses
 * uit de spec-C-enum (inclusief 0-tellingen).
 *
 * `gemiddeldeDoorlooptijdDagen` = gemiddelde van `ingediendOp` → moment van
 * statusovergang naar ACTIEF, over onboardings van de laatste 90 dagen.
 * Wanneer geen samples → 0 (niet `null`, om UI-gating te vereenvoudigen).
 *
 * Signalen worden gegroepeerd zodat de UI ze als 4 collapsible-blocks kan
 * renderen.
 */
export interface OnboardingDashboardStatsDTOv2 {
  totaalActief: number
  perStatus:    Record<OnboardingStatusValue, number>
  // batch-42 onboarding (FO-369) — telling per blokkade-reden ("wacht op X"),
  // over alle lopende onboardings. Bevat alle redenen (incl. 0-tellingen) zodat
  // de UI een stabiele lijst rendert. `GEEN` is per definitie 0 (een lopende
  // onboarding wacht altijd ergens op).
  blokkadePerReden: Record<OnboardingBlokkadeReden, number>
  gemiddeldeDoorlooptijdDagen: number
  signalen: {
    wachtOpMedewerker: SignaalWachtOpMedewerker[]
    deadlineNabij:     SignaalDeadlineNabij[]
    review4OgenOpen:   SignaalReview4OgenOpen[]
    assetsOntbreken:   SignaalAssetsOntbreken[]
  }
  recent: OnboardingDashboardRecentRow[]
}
